import {
  COMPONENTS,
  ROUTING_COMPONENT_ID,
  type ComponentState,
  type OverallState,
  type StatusComponentDef,
} from '@/components/status/data';
import { latencySummary } from './latency';

// ---- Kuma payload shapes (public status-page API, Kuma ≥ 2.0) ----
export type KumaHeartbeat = { status: 0 | 1 | 2 | 3; time: string; ping: number | null; msg?: string };
export type KumaIncident = {
  id: number;
  style: string;
  title: string;
  content: string;
  pin: boolean;
  active: boolean;
  createdDate: string;
  lastUpdatedDate: string | null;
};
export type KumaMaintenance = {
  id: number;
  title: string;
  description?: string;
  status?: string;
  timeslotList?: { startDate: string; endDate: string }[];
};
export type KumaStatusPage = {
  config: { title?: string; slug?: string };
  incidents?: KumaIncident[];
  publicGroupList: { id: number; name: string; monitorList: { id: number; name: string }[] }[];
  maintenanceList?: KumaMaintenance[];
};
export type KumaHeartbeatPayload = {
  heartbeatList: Record<string, KumaHeartbeat[]>;
  uptimeList: Record<string, number>;
};

// ---- Page model ----
export type Check = { t: string; status: 0 | 1 | 2 | 3; ping: number | null };
export type ComponentStatus = {
  id: StatusComponentDef['id'];
  label: string;
  description: string;
  state: ComponentState;
  uptime24: number | null;
  uptime30: number | null;
  checks: Check[];
  monitorId: number | null;
};
export type LatencyPoint = { t: string; ping: number | null };
export type StatusSnapshot = {
  overall: OverallState;
  updatedAt: string;
  source: 'kuma' | 'unavailable';
  components: ComponentStatus[];
  latency: { p50: number | null; p99: number | null; n: number; series: LatencyPoint[] };
  incidents: KumaIncident[];
  history: KumaIncident[];
  maintenance: KumaMaintenance[];
};

/** How many of the routing canary's checks feed the latency reading — one
 *  hour at Kuma's 60 s interval. */
export const LATENCY_WINDOW = 60;

/** Kuma stores "YYYY-MM-DD HH:mm:ss" in UTC without a zone marker. */
export function parseKumaTime(s: string): string {
  const m = s.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/);
  const iso = m ? `${m[1]}T${m[2]}Z` : s;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? s : d.toISOString();
}

/** The badge endpoint answers with an SVG whose last <text> is e.g. "99.98%". */
export function parseUptimeBadge(svg: string): number | null {
  const m = svg.match(/(\d{1,3}(?:\.\d+)?)%/);
  if (!m) return null;
  const pct = Number(m[1]);
  return Number.isFinite(pct) ? pct / 100 : null;
}

export function componentState(checks: Check[]): ComponentState {
  const last = checks[checks.length - 1];
  if (!last) return 'unmonitored';
  switch (last.status) {
    case 1:
      return 'operational';
    case 0:
      return 'down';
    case 2:
      return 'degraded';
    case 3:
      return 'maintenance';
  }
}

export function overallState(components: ComponentStatus[], maintenanceActive: boolean): OverallState {
  const monitored = components.filter((c) => c.state !== 'unmonitored');
  if (monitored.length === 0) return 'unknown';
  const routing = components.find((c) => c.id === ROUTING_COMPONENT_ID);
  if (routing?.state === 'down') return 'major';
  /*
   * The routing canary is the one monitor that proves a redirect still
   * resolves end to end. If it is missing from the status page, or present
   * but unmonitored, the other rows cannot vouch for the thing users
   * actually depend on — so the banner says "unknown", never "operational".
   * Green has to be earned by the canary, not inferred from its silence.
   */
  if (!routing || routing.state === 'unmonitored') return 'unknown';
  if (monitored.some((c) => c.state === 'down')) return 'partial';
  if (monitored.some((c) => c.state === 'degraded')) return 'degraded';
  if (maintenanceActive || monitored.some((c) => c.state === 'maintenance')) return 'maintenance';
  return 'operational';
}

export function unavailableSnapshot(now: Date = new Date()): StatusSnapshot {
  return {
    overall: 'unknown',
    updatedAt: now.toISOString(),
    source: 'unavailable',
    components: COMPONENTS.map((c) => ({
      id: c.id,
      label: c.label,
      description: c.description,
      state: 'unmonitored',
      uptime24: null,
      uptime30: null,
      checks: [],
      monitorId: null,
    })),
    latency: { p50: null, p99: null, n: 0, series: [] },
    incidents: [],
    history: [],
    maintenance: [],
  };
}

export function buildSnapshot(input: {
  page: KumaStatusPage | null;
  beats: KumaHeartbeatPayload | null;
  /** monitorId → 30-day uptime ratio (from the badge endpoint), null when unparsable. */
  badges: Record<number, number | null>;
  history: KumaIncident[];
  now?: Date;
}): StatusSnapshot {
  const now = input.now ?? new Date();
  if (!input.page) return unavailableSnapshot(now);

  const monitors = new Map<string, number>();
  for (const group of input.page.publicGroupList ?? []) {
    for (const m of group.monitorList ?? []) monitors.set(m.name, m.id);
  }

  const components: ComponentStatus[] = COMPONENTS.map((def) => {
    const monitorId = monitors.get(def.kumaName) ?? null;
    const raw = monitorId !== null ? input.beats?.heartbeatList?.[String(monitorId)] ?? [] : [];
    const checks: Check[] = raw.map((h) => ({ t: parseKumaTime(h.time), status: h.status, ping: h.ping ?? null }));
    const uptime24 = monitorId !== null ? input.beats?.uptimeList?.[`${monitorId}_24`] ?? null : null;
    const uptime30 = monitorId !== null ? input.badges[monitorId] ?? null : null;
    return {
      id: def.id,
      label: def.label,
      description: def.description,
      state: componentState(checks),
      uptime24,
      uptime30,
      checks,
      monitorId,
    };
  });

  const routing = components.find((c) => c.id === ROUTING_COMPONENT_ID);
  const window = (routing?.checks ?? []).slice(-LATENCY_WINDOW);
  const series: LatencyPoint[] = window.map((c) => ({ t: c.t, ping: c.status === 1 ? c.ping : null }));
  const summary = latencySummary(series.map((p) => p.ping));

  const active = (input.page.incidents ?? []).filter((i) => i.active);
  const activeIds = new Set(active.map((i) => i.id));
  const history = input.history.filter((i) => !i.active && !activeIds.has(i.id));
  const maintenance = input.page.maintenanceList ?? [];
  const maintenanceActive = maintenance.some((m) => (m.status ?? '').toLowerCase() === 'under-maintenance');

  return {
    overall: overallState(components, maintenanceActive),
    updatedAt: now.toISOString(),
    source: 'kuma',
    components,
    latency: { ...summary, series },
    incidents: active,
    history,
    maintenance,
  };
}
